import React, { useState } from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import {
  SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES,
  SSD_ROLE,
  SSD_ROLE_NAME,
} from "../SettingsConstant";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import TimePicker from "@amzn/meridian/time-picker";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import minusCircleTokens from "@amzn/meridian-tokens/base/icon/minus-circle";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import { ModalFooter } from "@amzn/meridian/modal";
import {
  isTimeWindowOverlappingWithOthers,
  isValidTimeFormat,
} from "../../SettingsUtil";
import { TIME_PATTERN } from "../../../util/Constants";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import { isValidNumber } from "../../../util/Util";

const UpsertVolumeRatioRule = ({
  rules,
  rule,
  onSubmit,
  onCancel,
  isEditRule,
}) => {
  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const [updatedRule, setUpdatedRule] = useState(
    JSON.parse(JSON.stringify(rule)),
  );
  const [groupedTimeWindows, setGroupedTimeWindows] = useState(
    getGroupedTimeWindows(JSON.parse(JSON.stringify(rule))),
  );
  const availableRoleNames = Object.values(SSD_ROLE_NAME).filter(
    (roleName) => rules.filter(({ name }) => name === roleName).length === 0,
  );
  if (updatedRule.name && !availableRoleNames.includes(updatedRule.name)) {
    availableRoleNames.push(updatedRule.name);
  }
  const isDayOfWeekConfigurationEnabled =
    SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES.includes(updatedRule.name);

  const onChangeRole = (val) => {
    updatedRule.name = val;
    updatedRule.label = SSD_ROLE[val].roleLabel;
    updatedRule.department_name = SSD_ROLE[val].departmentName;
    updatedRule.process = SSD_ROLE[val].processName;
    updatedRule.businessType = SSD_ROLE[val].businessType;
    updatedRule.speed = SSD_ROLE[val].speed;
    updatedRule.direct_role = SSD_ROLE[val].isDirectRole;
    updatedRule.volume_type = SSD_ROLE[val].volumeType;
    updatedRule.order = SSD_ROLE[val].order;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeDayOfWeek = (groupedTimeWindow, val) => {
    groupedTimeWindow.isEnabledByDay.forEach((_, i) => {
      groupedTimeWindow.isEnabledByDay[i] = val.includes(i);
    });
    setGroupedTimeWindows([...groupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      groupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeStartTime = (groupedTimeWindow, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      groupedTimeWindow.start = val.slice(0, -3);
      setGroupedTimeWindows([...groupedTimeWindows]);
      updatedRule.value = getRuleTimeWindows(
        groupedTimeWindows,
        isDayOfWeekConfigurationEnabled,
      );
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onChangeEndTime = (groupedTimeWindow, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      groupedTimeWindow.end = val.slice(0, -3);
      setGroupedTimeWindows([...groupedTimeWindows]);
      updatedRule.value = getRuleTimeWindows(
        groupedTimeWindows,
        isDayOfWeekConfigurationEnabled,
      );
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onChangeReferenceRole = (groupedTimeWindow, val) => {
    groupedTimeWindow.percentage_of_process_role = val;
    setGroupedTimeWindows([...groupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      groupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onChangePerNoOfPackages = (groupedTimeWindow, val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    groupedTimeWindow.perNoOfPackages = val;
    setGroupedTimeWindows([...groupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      groupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onAddTimeWindow = () => {
    groupedTimeWindows.push({
      perNoOfPackages: 0,
      percentage_of_process_role: null,
      start: "00:00",
      end: "23:59",
      isEnabledByDay: [true, true, true, true, true, true, true],
    });
    setGroupedTimeWindows([...groupedTimeWindows]);
  };

  const onDeleteTimeWindow = (groupedTimeWindow) => {
    const updatedGroupedTimeWindows = groupedTimeWindows.filter(
      (gtw) => gtw !== groupedTimeWindow,
    );
    setGroupedTimeWindows([...updatedGroupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      updatedGroupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onClickSubmit = () => {
    if (!updatedRule.name) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select a role.`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(
        ({ perNoOfPackages }) =>
          !isValidNumber(perNoOfPackages) || perNoOfPackages <= 0,
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a non-zero value for number of packages`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(
        ({ percentage_of_process_role }) => !percentage_of_process_role,
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter the missing roles`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(
        ({ start, end }) =>
          !isValidTimeFormat(start + ":00", TIME_PATTERN) ||
          !isValidTimeFormat(end + ":00", TIME_PATTERN),
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter the missing start and end times.`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(({ start, end }) => start === end).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Start and end times cannot be identical`,
        }),
      );
    }
    if (isGroupedTimeWindowsOverlapping(groupedTimeWindows)) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter non-overlapping time windows.`,
        }),
      );
    }
    onSubmit(updatedRule);
    onCancel();
  };

  return (
    <Column>
      <React.Fragment>
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {toast.message}
            </Alert>
          )}
        </Toaster>
      </React.Fragment>
      <Row alignmentHorizontal="center">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Role *
          </Text>
          <Select
            value={updatedRule.name}
            onChange={onChangeRole}
            placeholder="Select a role"
            width={200}
            disabled={isEditRule}
          >
            {availableRoleNames.map((roleName) => (
              <SelectOption
                value={roleName}
                label={SSD_ROLE[roleName].roleLabel}
              />
            ))}
          </Select>
        </Column>
      </Row>
      <div
        style={{ width: "100%", height: 1, backgroundColor: "rgba(0,0,0,0.1)" }}
      />
      {groupedTimeWindows.map((groupedTimeWindow, i) => {
        const isWeekWide =
          groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
            .length === 0;
        return (
          <Row width="100%">
            <Column>
              <Text tag="label" type="b200" color="primary">
                Number of Packages *
              </Text>
              <Input
                value={groupedTimeWindow.perNoOfPackages}
                onChange={(val) =>
                  onChangePerNoOfPackages(groupedTimeWindow, val)
                }
                type="number"
                width={150}
                suffix={"Pkgs"}
              />
            </Column>
            <Column>
              <Row height={15} />
              <Text>Of</Text>
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                Role
              </Text>
              <Select
                value={groupedTimeWindow.percentage_of_process_role}
                onChange={(val) =>
                  onChangeReferenceRole(groupedTimeWindow, val)
                }
                placeholder="Select a role"
                width={200}
              >
                {Object.values(SSD_ROLE).map(({ roleName, roleLabel }) => (
                  <SelectOption value={roleName} label={roleLabel} />
                ))}
              </Select>
            </Column>
            {(!isWeekWide || isDayOfWeekConfigurationEnabled) && (
              <Column>
                <Text tag="label" type="b200" color="primary">
                  Day(s) Select*
                </Text>
                <ButtonGroup
                  value={groupedTimeWindow.isEnabledByDay
                    .map((isEnabled, i) => (isEnabled ? i : -1))
                    .filter((i) => i >= 0)}
                  onChange={(val) => onChangeDayOfWeek(groupedTimeWindow, val)}
                  size="medium"
                >
                  <ButtonOption value={0}>S</ButtonOption>
                  <ButtonOption value={1}>M</ButtonOption>
                  <ButtonOption value={2}>T</ButtonOption>
                  <ButtonOption value={3}>W</ButtonOption>
                  <ButtonOption value={4}>T</ButtonOption>
                  <ButtonOption value={5}>F</ButtonOption>
                  <ButtonOption value={6}>S</ButtonOption>
                </ButtonGroup>
              </Column>
            )}
            <Column>
              <Text tag="label" type="b200" color="primary">
                Start time*
              </Text>
              <TimePicker
                placeholder="MM:HH"
                value={groupedTimeWindow.start + ":00"}
                onChange={(val) => onChangeStartTime(groupedTimeWindow, val)}
                width={200}
              />
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                End time*
              </Text>
              <TimePicker
                placeholder="MM:HH"
                value={groupedTimeWindow.end + ":00"}
                onChange={(val) => onChangeEndTime(groupedTimeWindow, val)}
                width={200}
              />
            </Column>
            <Column>
              <Row height={15} />
              <Row>
                {groupedTimeWindows.length > 1 && (
                  <Button
                    type="icon"
                    onClick={() => onDeleteTimeWindow(groupedTimeWindow)}
                  >
                    <Text color="error">
                      <Icon tokens={minusCircleTokens} />
                    </Text>
                  </Button>
                )}
                {i === groupedTimeWindows.length - 1 && (
                  <Button type="icon" onClick={onAddTimeWindow}>
                    <div style={{ color: "#63a63a" }}>
                      <Icon tokens={plusCircleTokens} />
                    </div>
                  </Button>
                )}
              </Row>
            </Column>
          </Row>
        );
      })}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onCancel}>
            Close
          </Button>
          <Button type="primary" size="small" onClick={onClickSubmit}>
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Column>
  );
};

const getGroupedTimeWindows = (rule) => {
  if (!Array.isArray(rule.value)) {
    return [];
  }
  const timeWindows = [];
  timeWindows.push(...rule.value);
  const timeWindowMap = {};
  timeWindows.forEach((timeWindow) => {
    const {
      day_of_week,
      start,
      end,
      percentage_of_process_role,
      per_no_of_packages,
    } = timeWindow;
    if (
      !Object.values(SSD_ROLE_NAME).includes(percentage_of_process_role) &&
      percentage_of_process_role != null
    ) {
      return false;
    }
    const key = start + "-" + end;
    if (!timeWindowMap[key]) {
      timeWindowMap[key] = {
        perNoOfPackages: per_no_of_packages,
        percentage_of_process_role,
        start,
        end,
        isEnabledByDay: [false, false, false, false, false, false, false],
      };
    }
    if (day_of_week == null) {
      timeWindowMap[key].isEnabledByDay = [
        true,
        true,
        true,
        true,
        true,
        true,
        true,
      ];
    } else {
      timeWindowMap[key].isEnabledByDay[day_of_week] = true;
    }
  });
  return Object.values(timeWindowMap);
};

const isGroupedTimeWindowsOverlapping = (groupedTimeWindows) => {
  return (
    groupedTimeWindows.filter((groupedTimeWindow) => {
      const otherGroupedTimeWindows = groupedTimeWindows.filter(
        (otherGroupedTimeWindow) =>
          otherGroupedTimeWindow !== groupedTimeWindow,
      );
      return isTimeWindowOverlappingWithOthers(
        {
          startTime: groupedTimeWindow.start + ":00",
          endTime: groupedTimeWindow.end + ":00",
        },
        otherGroupedTimeWindows.map(({ start, end }) => ({
          startTime: start + ":00",
          endTime: end + ":00",
        })),
      );
    }).length > 0
  );
};

const getRuleTimeWindows = (
  groupedTimeWindows,
  isDayOfWeekConfigurationEnabled,
) => {
  const timeWindows = [];
  groupedTimeWindows.forEach((groupedTimeWindow) => {
    if (!groupedTimeWindow.perNoOfPackages) {
      return false;
    }
    if (!groupedTimeWindow.percentage_of_process_role) {
      return false;
    }
    if (!groupedTimeWindow.start || !groupedTimeWindow.end) {
      return false;
    }
    const isWeekWide =
      groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
        .length === 0;
    const timeWindow = {
      value: 1.0 / parseFloat(groupedTimeWindow.perNoOfPackages),
      per_no_of_packages: parseInt(groupedTimeWindow.perNoOfPackages, 10),
      percentage_of_process_path:
        SSD_ROLE[groupedTimeWindow.percentage_of_process_role].departmentName,
      percentage_of_process_role: groupedTimeWindow.percentage_of_process_role,
      percentage_of_process:
        SSD_ROLE[groupedTimeWindow.percentage_of_process_role].processName,
      start: groupedTimeWindow.start,
      end: groupedTimeWindow.end,
    };
    if (isWeekWide && !isDayOfWeekConfigurationEnabled) {
      timeWindows.push({ ...timeWindow });
    } else {
      groupedTimeWindow.isEnabledByDay.forEach((isEnabled, i) => {
        if (isEnabled) {
          timeWindows.push({
            ...timeWindow,
            day_of_week: i,
          });
        }
      });
    }
  });
  return timeWindows;
};

export default UpsertVolumeRatioRule;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/UpsertVolumeRatioRule.js